import {guard, merge, sample} from 'effector';

import {RestError, showRestError} from 'core/errors';

import {
    assignAppealFx,
    assignRequested,
    data$,
    employees$,
    error$,
    fetchDataFx,
    fetchEmployeesFx,
    fetchThemesFx,
    Gate,
    rejectAppealFx,
    rejectRequested,
    resolveAppealFx,
    resolveRequested,
    responsePhotosSelected,
    setEmployees,
    takeAppealFx,
    takeRequested,
    themes$,
    uploadResponsePhotosFx,
} from './index';

data$.on(fetchDataFx.doneData, (_, data) => data).reset(Gate.close);
employees$.on(fetchEmployeesFx.doneData, (_, data) => data).reset(Gate.close);
themes$.on(fetchThemesFx.doneData, (_, data) => data).reset(Gate.close);
employees$.on(setEmployees, (_, data) => data).reset(Gate.close);
error$
    .on(fetchDataFx.failData, (_, error) => error)
    .reset(fetchDataFx, fetchDataFx.done, Gate.close);

sample({clock: Gate.open, fn: ({id}) => id, target: fetchDataFx});

sample({
    clock: takeRequested,
    source: Gate.state,
    fn: ({id}, theme) => ({id, theme}),
    target: takeAppealFx,
});
sample({
    clock: resolveRequested,
    source: Gate.state,
    fn: ({id}, response) => ({id, response}),
    target: resolveAppealFx,
});
sample({
    clock: rejectRequested,
    source: Gate.state,
    fn: ({id}, response) => ({id, response}),
    target: rejectAppealFx,
});
sample({
    clock: assignRequested,
    source: Gate.state,
    fn: ({id}, request) => ({id, request}),
    target: assignAppealFx,
});
sample({
    clock: responsePhotosSelected,
    source: Gate.state,
    fn: ({id}, files) => ({id, files}),
    target: uploadResponsePhotosFx,
});

const assignmentDataLoaded = sample({
    clock: fetchDataFx.doneData,
    source: Gate.state,
    fn: (gate, data) => ({gate, data}),
});

const employeesRequested = guard({
    source: assignmentDataLoaded,
    filter: ({gate, data}) => Boolean(data.canAssign && gate.canViewPersonnel),
});
const employeesSet = guard({
    source: assignmentDataLoaded,
    filter: ({gate, data}) => Boolean(!data.canAssign || !gate.canViewPersonnel),
});
sample({
    clock: employeesSet,
    fn: ({data}) => [
        {
            userId: data.appeal.assignedTo as number,
            lastName: data.assigneeName as string,
        },
    ],
    target: setEmployees,
});
sample({clock: employeesRequested, fn: () => undefined, target: fetchEmployeesFx});
sample({clock: Gate.open, target: fetchThemesFx});

sample({
    clock: merge([
        takeAppealFx.done,
        resolveAppealFx.done,
        rejectAppealFx.done,
        assignAppealFx.done,
        uploadResponsePhotosFx.done,
    ]),
    source: Gate.state,
    fn: ({id}) => id,
    target: fetchDataFx,
});

[
    takeAppealFx,
    resolveAppealFx,
    rejectAppealFx,
    assignAppealFx,
    fetchEmployeesFx,
    fetchThemesFx,
    uploadResponsePhotosFx,
].forEach((effect) => effect.failData.watch((error) => showRestError(error as RestError)));
